import { useEffect, useState } from 'react';
import { Badge, Card, Empty, MapLegend, Page, RideMap, rideStatusLabel, type MapMarker } from '@mobi/web';
import { api } from '@mobi/web';

interface LiveDriver {
  driverId: number;
  lat: number;
  lng: number;
  vehicleType: 'car' | 'moto';
  rideId: number | null;
}

interface LiveRide {
  id: number;
  status: string;
  vehicleType: 'car' | 'moto';
  passengerName: string;
  driverName: string | null;
  driverId: number | null;
  origin: { lat: number; lng: number };
  destination: { lat: number; lng: number };
}

const POLL_MS = 5_000;

export function Live() {
  const [drivers, setDrivers] = useState<LiveDriver[]>([]);
  const [rides, setRides] = useState<LiveRide[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await api<{ drivers: LiveDriver[]; rides: LiveRide[] }>('/api/admin/rides/live');
        if (!cancelled) {
          setDrivers(res.drivers);
          setRides(res.rides);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const markers: MapMarker[] = [
    ...drivers.map((d) => ({
      id: `d${d.driverId}`,
      point: { lat: d.lat, lng: d.lng },
      kind: 'driver' as const,
      vehicleType: d.vehicleType,
    })),
    ...rides.flatMap((r) => [
      { id: `o${r.id}`, point: r.origin, kind: 'origin' as const },
      { id: `x${r.id}`, point: r.destination, kind: 'destination' as const },
    ]),
  ];

  return (
    <Page title="Corridas ao vivo" subtitle="Motoristas conectados agora e corridas em andamento.">
      {!loading && markers.length === 0 ? (
        <Empty title="Nada acontecendo agora" hint="Assim que alguém ficar online ou pedir uma corrida, aparece aqui." />
      ) : (
        <>
          <RideMap markers={markers} className="mb-3 h-96 w-full overflow-hidden rounded-3xl border border-line" />
          <MapLegend
            items={[
              { kind: 'driver', label: 'Motorista online' },
              { kind: 'origin', label: 'Origem' },
              { kind: 'destination', label: 'Destino' },
            ]}
          />
        </>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-2 text-sm font-semibold text-muted">Motoristas online ({drivers.length})</p>
          <Card className="divide-y divide-line p-0">
            {drivers.length === 0 ? (
              <p className="px-5 py-4 text-sm text-faint">nenhum</p>
            ) : (
              drivers.map((d) => (
                <div key={d.driverId} className="flex items-center justify-between px-5 py-3 text-sm">
                  <span>#{d.driverId} · {d.vehicleType === 'car' ? 'carro' : 'moto'}</span>
                  <Badge tone={d.rideId ? 'brand' : 'good'}>{d.rideId ? `em corrida #${d.rideId}` : 'livre'}</Badge>
                </div>
              ))
            )}
          </Card>
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-muted">Corridas em andamento ({rides.length})</p>
          <Card className="divide-y divide-line p-0">
            {rides.length === 0 ? (
              <p className="px-5 py-4 text-sm text-faint">nenhuma</p>
            ) : (
              rides.map((r) => (
                <div key={r.id} className="px-5 py-3 text-sm">
                  <p className="font-medium">#{r.id} · {r.passengerName} → {r.driverName ?? 'sem motorista'}</p>
                  <p className="text-faint">{rideStatusLabel(r.status)}</p>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>
    </Page>
  );
}
